import { watch, onUnmounted } from "vue"

/**
 * CoreUI `CModal` with `backdrop="static"` does not call `handleDismiss` on Escape
 * (see `@coreui/vue-pro` CModal `handleKeyUp`). Wire Escape so keyboard users can
 * dismiss the same way as non-static modals.
 *
 * @param {() => boolean} isVisibleGetter - reactive, e.g. () => state.isModalOpen
 * @param {() => void} onClose - closes modal and resets state as needed
 */
export function useEscapeToCloseModal(isVisibleGetter, onClose) {
  const onKeydown = (event) => {
    if (event.key !== "Escape") return
    if (!isVisibleGetter()) return
    event.preventDefault()
    onClose()
  }

  const addEscapeListener = () => {
    if (typeof document === "undefined") return
    document.addEventListener("keydown", onKeydown, true)
  }

  const removeEscapeListener = () => {
    if (typeof document === "undefined") return
    document.removeEventListener("keydown", onKeydown, true)
  }

  watch(
    () => isVisibleGetter(),
    (visible) => {
      if (visible) {
        addEscapeListener()
      } else {
        removeEscapeListener()
      }
    },
    { flush: "post", immediate: true }
  )

  onUnmounted(() => {
    removeEscapeListener()
  })
}
